import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { fetchInventories } from '../../../ReduxSlices/Inventories/inventorySlice';


const PrescriptionEditor = ({
  onSubmit, submitting, onCancel, isEditing, setMedicationItems, medicationItems,setDeletedMedicationIds
}) => {
 const dispatch = useDispatch();

 useEffect(() => {
    dispatch(fetchInventories());
}, [dispatch]);

 const inventories = useSelector((state) => state.inventories.inventories);
 console.log("data found",inventories)

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [dosage, setDosage] = useState('');
  const [frequency, setFrequency] = useState('');
  const [duration, setDuration] = useState('');
  const [instruction, setInstruction] = useState('');
  const [errors, setErrors] = useState({});
  const [editIndex, setEditIndex] = useState(null);
  
  
  // Filter inventory based on search term
const filteredInventory = useMemo(() => {
  const term = searchTerm.toLowerCase();
  return inventories.filter((item) => {
    const name = item.name || '';
    const code = item.medication_code || '';
    return name.toLowerCase().includes(term) || code.toLowerCase().includes(term);
  });
}, [searchTerm, inventories]);


 const handleAddMedication = () => {
      const newErrors = {};

      if (!selectedInventory) newErrors.inventory = 'Please select a medication';
      if (!dosage.trim()) newErrors.dosage = 'Dosage is required';
      if (!frequency.trim()) newErrors.frequency = 'Frequency is required';
      if (!duration.trim()) newErrors.duration = 'Duration is required';

      setErrors(newErrors);

      // stop submission if there are errors
      if (Object.keys(newErrors).length > 0) return;

      const newItem = {
        id: editIndex !== null ? medicationItems[editIndex].id : undefined,
        inventory_id: selectedInventory.id,
        medication_name: selectedInventory.name,
        dosage,
        frequency,
        duration,
        instruction,
      };

    if (editIndex !== null) {
    // Update existing medication
    setMedicationItems((prev) => {
      const updated = [...prev];
      updated[editIndex] = newItem;
      return updated;
    });
    setEditIndex(null); // exit edit mode
  } else {
    // Add new medication
    setMedicationItems((prev) => [...prev, newItem]);
  }

      // clear inputs and errors
      setSelectedInventory(null);
      setSearchTerm('');
      setDosage('');
      setFrequency('');
      setDuration('');
      setInstruction('');
      setErrors({});
  };


const handleRemove = (index) => {
  const removedItem = medicationItems[index];
  if (removedItem.id) {
    setDeletedMedicationIds(prev => [...prev, removedItem.id]);
  }
  setMedicationItems(prev => prev.filter((_, i) => i !== index));
};


  const handleEditMedication = (index) => {
  const item = medicationItems[index];
  setSelectedInventory({ id: item.inventory_id, name: item.medication_name });
  setDosage(item.dosage);
  setFrequency(item.frequency);
  setDuration(item.duration);
  setInstruction(item.instruction || '');
  setEditIndex(index);
};

const handleCancel = () => {
  setSelectedInventory(null);
  setSearchTerm('');
  setDosage('');
  setFrequency('');
  setDuration('');
  setInstruction('');
  setErrors({});
  setEditIndex(null);
  onCancel();
};

  let buttonText = 'Save Prescription';
  if (submitting) buttonText = 'Saving...';
  else if (isEditing) buttonText = 'Update Prescription';

  return (
    <div className="add space-y-4">
      <h3 className="text-lg font-semibold">{isEditing ? 'Edit Prescription' : 'Add New Prescription'}</h3>

      <form className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* Searchable Inventory Select */}
        <div className="search-dropdown">
          <input
            type="text"
            placeholder="Search medication..."
            value={selectedInventory ? selectedInventory.name : searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelectedInventory(null); // clear selection if user types again
              setErrors((prev) => ({ ...prev, inventory: '' }));
            }}
            className={`search-input ${errors.inventory ? 'border-red-500' : ''}`}
          />

          {/* Show dropdown only if user is typing and nothing is selected */}
          {searchTerm && !selectedInventory && filteredInventory.length > 0 && (
          <ul className="search-list">
            {filteredInventory.map((inv) => (
              <li
                key={inv.id}
                onClick={() => {
                  setSelectedInventory(inv); // select item
                  setSearchTerm(''); // clear search so dropdown disappears
                  setErrors((prev) => ({ ...prev, inventory: '' }));
                }}
                className="search-list-item"
              >
                {inv.name}
                {' '}
                (Stock:
                {inv.quantity}
                )
              </li>
            ))}
          </ul>
          )}
          {errors.inventory && <p className="error-msg">{errors.inventory}</p>}
        </div>

        <div>
        <input
          type="text"
          placeholder="Dosage (e.g. 500mg)"
          value={dosage}
          onChange={(e) => {
            setDosage(e.target.value)
            setErrors((prev) => ({ ...prev, dosage: '' }));
          }}
          className={`border p-2 rounded w-full ${errors.dosage ? 'border-red-500' : ''}`}
        />
        {errors.dosage && <p className="error-msg">{errors.dosage}</p>}
     </div>

       <div>
        <input
          type="text"
          placeholder="Frequency (e.g. twice daily)"
          value={frequency}
          onChange={(e) => { 
            setFrequency(e.target.value);
            setErrors((prev) => ({ ...prev, frequency: '' }));
          }}
        className={`border p-2 rounded w-full ${errors.frequency ? 'border-red-500' : ''}`}
        />
         {errors.frequency && <p className="error-msg">{errors.frequency}</p>}
        </div>

        <div>
        <input
          type="text"
          placeholder="Duration (e.g. 5 days)"
          value={duration}
          onChange={(e) => {
            setDuration(e.target.value);
            setErrors((prev) => ({ ...prev, duration: '' }));

          }}
           className={`border p-2 rounded w-full ${errors.duration ? 'border-red-500' : ''}`}
        />
        {errors.duration && <p className="error-msg">{errors.duration}</p>}
        </div>


        <input
          type="text"
          placeholder="Instructions (optional)"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          className="border p-2 rounded col-span-2"
        />

        <button
          type="button"
          onClick={handleAddMedication}
          className="primary"
        >
          {editIndex !== null ? 'Update Medication' : 'Add Medication'}
        </button>
      </form>

      {/* Medication List */}
      {medicationItems.length > 0 && (
        <div>
          <h3 className="font-semibold mt-4">Medication List</h3>
          <ol className="prescription-list-items">
            {medicationItems.map((item, index) => (
              <li key={index} className="mb-1 flex gap-2">
                <strong>{item.medication_name}</strong>
                {' '}
                —
                {item.dosage}
                ,
                {item.frequency}
                ,
                {item.duration}
                {item.instruction && ` (${item.instruction})`}

              <div className='ml-[20px] action-btns'>
               <button
                  type="button"
                  onClick={() => handleEditMedication(index)}
                  className="ml-2 text-sm  text-white px-2 py-1 rounded"
                >
                  Edit
                </button>

                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="ml-[10px] primary text-sm"
                >
                  Remove
                </button>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="flex save-prescription-btns">
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className="primary wide"
        >
          {buttonText}
        </button>
        {isEditing && (
          <button
            onClick={handleCancel}
            type="button"
            className="bg-gray-400 text-white px-4 py-2 rounded"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

PrescriptionEditor.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  medicationItems: PropTypes.array.isRequired,
  setMedicationItems: PropTypes.func.isRequired,
  setDeletedMedicationIds: PropTypes.func.isRequired
};

export default PrescriptionEditor;
